/**
 * Health Map with District Polygons
 *
 * Uses GeoJSON boundaries to show district areas colored by risk level
 */

import { useEffect, useState } from 'react'
import { APIProvider, Map as GoogleMap, useMap } from '@vis.gl/react-google-maps'
import { AlertTriangle, Activity, CheckCircle } from 'lucide-react'
import { MapErrorBoundary } from './MapErrorBoundary'
import { logger } from '@/shared/utils/logger'

interface DistrictHealth {
  id: string
  name_th: string
  name_en: string
  risk_level: 'low' | 'medium' | 'high'
  active_cases: number
  coverage_rate: number
  ncd_patients: number
}

interface HealthMapPolygonProps {
  districts?: DistrictHealth[]
  className?: string
  year?: number
  selectedDistrictId?: number | null
  selectedDistrictName?: string | null
}

// Mock district data - names must match GADM GeoJSON exactly
const MOCK_DISTRICTS: DistrictHealth[] = [
  { id: '1', name_th: 'เมืองนครราชสีมา', name_en: 'MuangNakhonRatchasima', risk_level: 'high', active_cases: 1247, coverage_rate: 98.5, ncd_patients: 8942 },
  { id: '2', name_th: 'ครบุรี', name_en: 'KhonBuri', risk_level: 'medium', active_cases: 423, coverage_rate: 96.2, ncd_patients: 3214 },
  { id: '3', name_th: 'เสิงสาง', name_en: 'SoengSang', risk_level: 'low', active_cases: 89, coverage_rate: 97.8, ncd_patients: 1245 },
  { id: '4', name_th: 'คง', name_en: 'Khong', risk_level: 'low', active_cases: 67, coverage_rate: 95.3, ncd_patients: 987 },
  { id: '5', name_th: 'บ้านเหลื่อม', name_en: 'BanLuam', risk_level: 'medium', active_cases: 234, coverage_rate: 94.7, ncd_patients: 2134 },
  { id: '6', name_th: 'จักราช', name_en: 'Chakkarat', risk_level: 'low', active_cases: 112, coverage_rate: 96.8, ncd_patients: 1876 },
  { id: '7', name_th: 'โชคชัย', name_en: 'ChokChai', risk_level: 'medium', active_cases: 345, coverage_rate: 97.2, ncd_patients: 2987 },
  { id: '8', name_th: 'ด่านขุนทด', name_en: 'DanKhunThot', risk_level: 'high', active_cases: 567, coverage_rate: 95.9, ncd_patients: 4321 },
  { id: '9', name_th: 'โนนไทย', name_en: 'NonThai', risk_level: 'low', active_cases: 98, coverage_rate: 96.5, ncd_patients: 1543 },
  { id: '10', name_th: 'โนนสูง', name_en: 'NonSung', risk_level: 'medium', active_cases: 276, coverage_rate: 97.1, ncd_patients: 2456 },
  { id: '11', name_th: 'ขามสะแกแสง', name_en: 'KhamSakaeSaeng', risk_level: 'low', active_cases: 134, coverage_rate: 96.3, ncd_patients: 1892 },
  { id: '12', name_th: 'บัวใหญ่', name_en: 'BuaYai', risk_level: 'medium', active_cases: 298, coverage_rate: 95.8, ncd_patients: 2678 },
  { id: '13', name_th: 'ประทาย', name_en: 'PraThai', risk_level: 'low', active_cases: 87, coverage_rate: 97.4, ncd_patients: 1234 },
  { id: '14', name_th: 'ปักธงชัย', name_en: 'PakThongChai', risk_level: 'high', active_cases: 489, coverage_rate: 96.7, ncd_patients: 3876 },
  { id: '15', name_th: 'พิมาย', name_en: 'Phimai', risk_level: 'medium', active_cases: 312, coverage_rate: 97.0, ncd_patients: 2765 },
  { id: '16', name_th: 'ห้วยแถลง', name_en: 'HuaiThalang', risk_level: 'low', active_cases: 145, coverage_rate: 95.6, ncd_patients: 1987 },
  { id: '17', name_th: 'ปากช่อง', name_en: 'PakChong', risk_level: 'medium', active_cases: 423, coverage_rate: 96.8, ncd_patients: 3421 },
  { id: '18', name_th: 'ชุมพวง', name_en: 'ChumPhuang', risk_level: 'low', active_cases: 156, coverage_rate: 95.2, ncd_patients: 1876 },
  { id: '19', name_th: 'สีคิ้ว', name_en: 'Sikhiu', risk_level: 'low', active_cases: 189, coverage_rate: 94.9, ncd_patients: 1654 },
  { id: '20', name_th: 'เฉลิมพระเกียรติ', name_en: 'Chalermphrakiet', risk_level: 'low', active_cases: 98, coverage_rate: 93.7, ncd_patients: 1234 },
  { id: '21', name_th: 'สูงเนิน', name_en: 'SungNoen', risk_level: 'medium', active_cases: 267, coverage_rate: 95.5, ncd_patients: 2145 },
  { id: '22', name_th: 'โนนแดง', name_en: 'NonDaeng', risk_level: 'low', active_cases: 134, coverage_rate: 94.3, ncd_patients: 1543 },
  { id: '23', name_th: 'หนองบุญมาก', name_en: 'NongBunnak', risk_level: 'low', active_cases: 112, coverage_rate: 93.8, ncd_patients: 1321 },
  { id: '24', name_th: 'ขามทะเลสอ', name_en: 'KhamThalaSo', risk_level: 'low', active_cases: 89, coverage_rate: 92.9, ncd_patients: 987 },
  { id: '25', name_th: 'แก้งสนามนาง', name_en: 'KaengSanamNang', risk_level: 'low', active_cases: 76, coverage_rate: 92.1, ncd_patients: 876 },
  { id: '26', name_th: 'วังน้ำเขียว', name_en: 'WangNumKhiaw', risk_level: 'low', active_cases: 65, coverage_rate: 91.5, ncd_patients: 765 },
  { id: '27', name_th: 'เทพารักษ์', name_en: 'K.ThePhaRak', risk_level: 'low', active_cases: 54, coverage_rate: 90.8, ncd_patients: 654 },
  { id: '28', name_th: 'เมืองยาง', name_en: 'K.MuangYang', risk_level: 'low', active_cases: 43, coverage_rate: 90.2, ncd_patients: 543 },
  { id: '29', name_th: 'พระทองคำ', name_en: 'K.PhraThongKham', risk_level: 'low', active_cases: 32, coverage_rate: 89.5, ncd_patients: 432 },
  { id: '30', name_th: 'ลำทะเมนชัย', name_en: 'K.LamThaMenChai', risk_level: 'low', active_cases: 21, coverage_rate: 88.9, ncd_patients: 321 },
  { id: '31', name_th: 'บัวลาย', name_en: 'K.BuaLai', risk_level: 'low', active_cases: 15, coverage_rate: 88.2, ncd_patients: 234 },
  { id: '32', name_th: 'สีดา', name_en: 'K.Sida', risk_level: 'low', active_cases: 9, coverage_rate: 87.5, ncd_patients: 123 },
]

const getRiskColor = (level: 'low' | 'medium' | 'high', isHovered: boolean = false) => {
  const opacity = isHovered ? 0.85 : 0.65 // เพิ่ม opacity ให้เห็นชัดขึ้น
  switch (level) {
    case 'high':
      return `rgba(220, 38, 38, ${opacity})` // Red 600 - เด่นกว่าเดิม
    case 'medium':
      return `rgba(249, 115, 22, ${opacity})` // Orange 500 - เด่นกว่าเดิม
    case 'low':
      return `rgba(34, 197, 94, ${opacity})` // Green 500 - เข้มกว่าเดิม
  }
}

const getRiskLabel = (level: 'low' | 'medium' | 'high') => {
  switch (level) {
    case 'high': return 'สูง'
    case 'medium': return 'กลาง'
    case 'low': return 'ต่ำ'
  }
}

// Inner component that applies GeoJSON to the map
function MapDataLayer({
  districts,
  geoJsonData
}: {
  districts: DistrictHealth[]
  geoJsonData: any
}) {
  const map = useMap()

  // Map GeoJSON district names to our data
  const getDistrictData = (districtNameEn: string): DistrictHealth | undefined => {
    return districts.find(d => d.name_en === districtNameEn)
  }

  // Apply GeoJSON to map - removed hoveredDistrict from dependencies for performance
  useEffect(() => {
    if (!map || !geoJsonData) {
      logger.debug('Waiting for map or GeoJSON...', {
        hasMap: !!map,
        hasGeoJson: !!geoJsonData,
        mapType: map ? typeof map : 'undefined',
        geoJsonFeatures: geoJsonData ? geoJsonData.features?.length : 0
      })
      return
    }

    logger.debug('Map is ready! Applying district polygons...', {
      mapInstance: map,
      districtPolygons: geoJsonData.features.length,
      filteredDistricts: districts.length
    })

    // Clear existing data layers
    map.data.forEach(feature => map.data.remove(feature))

    // Filter GeoJSON features if specific districts are selected
    const districtNamesEn = districts.map(d => d.name_en)
    const filteredGeoJson = districts.length < 32 ? {
      ...geoJsonData,
      features: geoJsonData.features.filter((f: any) =>
        districtNamesEn.includes(f.properties.district_name_en)
      )
    } : geoJsonData

    logger.debug('Displaying districts:', {
      totalDistricts: geoJsonData.features.length,
      displayedDistricts: filteredGeoJson.features.length,
      selectedNames: districtNamesEn
    })

    // Add GeoJSON features (filtered or all)
    const addedFeatures = map.data.addGeoJson(filteredGeoJson)
    logger.debug(`Added ${addedFeatures.length} district polygons to map`)

    // Center map on filtered district if only one selected
    if (districts.length === 1 && filteredGeoJson.features.length > 0) {
      const bounds = new google.maps.LatLngBounds()
      filteredGeoJson.features.forEach((feature: any) => {
        if (feature.geometry.type === 'Polygon') {
          feature.geometry.coordinates[0].forEach((coord: number[]) => {
            bounds.extend({ lat: coord[1], lng: coord[0] })
          })
        } else if (feature.geometry.type === 'MultiPolygon') {
          feature.geometry.coordinates.forEach((polygon: number[][][]) => {
            polygon[0].forEach((coord: number[]) => {
              bounds.extend({ lat: coord[1], lng: coord[0] })
            })
          })
        }
      })
      map.fitBounds(bounds, { top: 50, right: 50, bottom: 50, left: 50 })
      logger.debug('Centered map on selected district:', districts[0].name_th)
    }

    // Track currently hovered feature for performance
    let currentHoveredFeature: google.maps.Data.Feature | null = null

    // Style features based on district data
    let matchCount = 0
    const unmatchedDistricts: string[] = []

    // Initial styling function - faster because no hover state
    const getFeatureStyle = (feature: google.maps.Data.Feature, isHovered: boolean = false) => {
      const districtNameEn = String(feature.getProperty('district_name_en'))
      const districtData = getDistrictData(districtNameEn)
      const riskLevel = districtData?.risk_level || 'low'

      if (districtData && !isHovered) {
        matchCount++
      } else if (!districtData && !unmatchedDistricts.includes(districtNameEn) && !isHovered) {
        unmatchedDistricts.push(districtNameEn)
      }

      return {
        fillColor: getRiskColor(riskLevel, isHovered),
        fillOpacity: isHovered ? 0.85 : 0.65,
        strokeColor: isHovered ? '#1e3a8a' : '#64748b', // Navy blue when hover, slate when normal
        strokeWeight: isHovered ? 3 : 1,
        strokeOpacity: isHovered ? 0.9 : 0.5,
      }
    }

    map.data.setStyle(feature => getFeatureStyle(feature, false))

    // Log matching results
    const districtNames = geoJsonData.features.map((f: any) => String(f.properties.district_name_en))
    const uniqueDistricts: string[] = Array.from(new Set(districtNames))
    const matchedDistricts = uniqueDistricts.filter(d => getDistrictData(d))
    logger.debug(`District matching: ${matchedDistricts.length}/${uniqueDistricts.length} districts matched`)

    if (unmatchedDistricts.length > 0) {
      const uniqueUnmatched = [...new Set(unmatchedDistricts)]
      logger.warn('Unmatched districts:', uniqueUnmatched.slice(0, 5).join(', ')
        + (uniqueUnmatched.length > 5 ? ` (+${uniqueUnmatched.length - 5} more)` : ''))
    }

    // Create InfoWindow once - reuse for performance
    const infoWindow = new google.maps.InfoWindow()

    // Hover effect - only update specific feature style
    const mouseoverListener = map.data.addListener('mouseover', (event: google.maps.Data.MouseEvent) => {
      const feature = event.feature
      const districtNameEn = String(feature.getProperty('district_name_en'))
      const districtNameTh = String(feature.getProperty('district_name_th'))
      const districtData = getDistrictData(districtNameEn)

      // Update only this feature's style - much faster!
      currentHoveredFeature = feature
      map.data.overrideStyle(feature, getFeatureStyle(feature, true))

      // Show info window
      if (districtData) {
        const content = `
          <div style="font-family: 'IBM Plex Sans Thai', sans-serif; padding: 8px; min-width: 200px;">
            <h3 style="font-weight: 600; font-size: 15px; margin-bottom: 8px; color: #1e3a8a;">${districtNameTh}</h3>
            <div style="font-size: 13px; line-height: 1.6;">
              <div style="margin-bottom: 4px;">
                <strong>ระดับความเสี่ยง:</strong>
                <span style="color: ${districtData.risk_level === 'high' ? '#dc2626' : districtData.risk_level === 'medium' ? '#f97316' : '#22c55e'}; font-weight: 600;">
                  ${getRiskLabel(districtData.risk_level)}
                </span>
              </div>
              <div style="margin-bottom: 4px;"><strong>ผู้ป่วย:</strong> ${String(districtData.active_cases.toLocaleString('th-TH'))} ราย</div>
              <div style="margin-bottom: 4px;"><strong>ครอบคลุม:</strong> ${String(districtData.coverage_rate)}%</div>
              <div><strong>โรคเรื้อรัง:</strong> ${String(districtData.ncd_patients.toLocaleString('th-TH'))} ราย</div>
            </div>
          </div>
        `
        infoWindow.setContent(content)
        infoWindow.setPosition(event.latLng)
        infoWindow.open(map)
      }
    })

    const mouseoutListener = map.data.addListener('mouseout', () => {
      // Reset style of previously hovered feature
      if (currentHoveredFeature) {
        map.data.revertStyle(currentHoveredFeature)
        currentHoveredFeature = null
      }
      infoWindow.close()
    })

    // Cleanup listeners
    return () => {
      google.maps.event.removeListener(mouseoverListener)
      google.maps.event.removeListener(mouseoutListener)
      infoWindow.close()
    }

  }, [map, geoJsonData, districts]) // Removed hoveredDistrict - performance boost!

  return null // This component doesn't render anything
}

function HealthMapPolygonInner({
  districts = MOCK_DISTRICTS,
  className = '',
  selectedDistrictId = null,
  selectedDistrictName = null
}: HealthMapPolygonProps) {
  const apiKey = import.meta.env.VITE_GOOGLE_MAPS_API_KEY || ''
  const [geoJsonData, setGeoJsonData] = useState<any>(null)

  // Load district-level GeoJSON data (32 districts from GADM)
  useEffect(() => {
    fetch('/geojson/nakhon_ratchasima_districts.json')
      .then(res => res.json())
      .then(data => {
        logger.debug(`District GeoJSON loaded: ${data.features.length} districts`)
        setGeoJsonData(data)
      })
      .catch(err => logger.error('Failed to load GeoJSON:', err))
  }, [])

  // Filter districts by name (not ID) to avoid mismatch between API and mock data
  const displayDistricts = selectedDistrictName
    ? districts.filter(d => {
        const match = d.name_th === selectedDistrictName
        if (match) {
          logger.debug('Filter matched by name:', {
            selectedDistrictName,
            districtId: d.id,
            districtName: d.name_th,
            districtNameEn: d.name_en
          })
        }
        return match
      })
    : districts

  logger.debug('Current filter state:', {
    selectedDistrictId,
    selectedDistrictName,
    totalDistricts: districts.length,
    displayedDistricts: displayDistricts.length,
    displayedNames: displayDistricts.map(d => d.name_th)
  })

  if (!apiKey) {
    return (
      <div className={`bg-amber-50 border border-amber-200 rounded-lg p-6 ${className}`}>
        <div className="flex items-start gap-3">
          <AlertTriangle className="w-5 h-5 text-amber-700 shrink-0 mt-0.5" />
          <div>
            <h3 className="text-sm font-semibold text-amber-800">ยังไม่ได้ตั้งค่า Google Maps API Key</h3>
            <p className="text-sm text-amber-700 mt-1">
              กรุณาเพิ่ม <code className="bg-amber-100 px-1 rounded">VITE_GOOGLE_MAPS_API_KEY</code> ในไฟล์ <code className="bg-amber-100 px-1 rounded">.env</code>
            </p>
          </div>
        </div>
      </div>
    )
  }

  const center = { lat: 15.0, lng: 102.1 }
  const zoom = selectedDistrictId ? 10 : 8.5

  return (
    <div className={className}>
      {/* Info: District-level polygons from GADM */}
      <div className="mb-4 bg-blue-50 border border-blue-200 rounded-lg p-3">
        <p className="text-sm text-blue-800 leading-relaxed">
          <strong>📍 หมายเหตุ:</strong> แผนที่แสดงขอบเขต<strong>ระดับอำเภอ</strong> (32 อำเภอ จากข้อมูล GADM)
          {selectedDistrictName && ` | 🎯 กรองแสดง: ${selectedDistrictName}`}
          <br />
          <span className="text-xs">💡 Hover เหนืออำเภอเพื่อดูข้อมูลสุขภาพ | ⚠️ ข้อมูล Mock รอ API จริง</span>
        </p>
      </div>

      <APIProvider apiKey={apiKey}>
        <div className="h-[500px] rounded-xl overflow-hidden border-2 border-slate-200 shadow-lg">
          <GoogleMap
            defaultCenter={center}
            defaultZoom={zoom}
            disableDefaultUI={false}
            gestureHandling="greedy"
          >
            {geoJsonData && (
              <MapDataLayer
                districts={displayDistricts}
                geoJsonData={geoJsonData}
              />
            )}
          </GoogleMap>
        </div>
      </APIProvider>

      {/* Legend */}
      <div className="mt-4 bg-white rounded-lg border border-slate-200 p-4">
        <h3 className="text-sm font-semibold text-korat-navy-700 mb-3">ระดับความเสี่ยง</h3>
        <div className="grid grid-cols-3 gap-4">
          <div className="flex items-center gap-2">
            <div className="w-8 h-8 rounded border-2 border-slate-300 shadow-sm" style={{ backgroundColor: 'rgba(34, 197, 94, 0.65)' }} />
            <div>
              <div className="text-xs font-medium text-slate-900">ต่ำ (Low)</div>
              <div className="text-xs text-slate-500">
                {displayDistricts.filter(d => d.risk_level === 'low').length} อำเภอ
              </div>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <div className="w-8 h-8 rounded border-2 border-slate-300 shadow-sm" style={{ backgroundColor: 'rgba(249, 115, 22, 0.65)' }} />
            <div>
              <div className="text-xs font-medium text-slate-900">กลาง (Medium)</div>
              <div className="text-xs text-slate-500">
                {displayDistricts.filter(d => d.risk_level === 'medium').length} อำเภอ
              </div>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <div className="w-8 h-8 rounded border-2 border-slate-300 shadow-sm" style={{ backgroundColor: 'rgba(220, 38, 38, 0.65)' }} />
            <div>
              <div className="text-xs font-medium text-slate-900">สูง (High)</div>
              <div className="text-xs text-slate-500">
                {displayDistricts.filter(d => d.risk_level === 'high').length} อำเภอ
              </div>
            </div>
          </div>
        </div>
        <p className="text-xs text-slate-500 mt-3">
          💡 Hover เหนือแผนที่เพื่อดูรายละเอียดแต่ละอำเภอ
        </p>
      </div>

      {/* Statistics */}
      <div className="mt-4 grid grid-cols-1 md:grid-cols-3 gap-4">
        <div className="bg-white rounded-lg border border-slate-200 p-4">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg bg-korat-orange-100 flex items-center justify-center">
              <Activity className="w-5 h-5 text-korat-orange-600" />
            </div>
            <div>
              <div className="text-xs text-slate-600">ผู้ป่วยรวม</div>
              <div className="text-xl font-bold text-korat-navy-700 tabular-nums">
                {displayDistricts.reduce((sum, d) => sum + d.active_cases, 0).toLocaleString('th-TH')}
              </div>
              <div className="text-xs text-slate-500">ราย</div>
              <div className="text-xs text-slate-400 mt-1">จำนวนผู้ป่วยในพื้นที่ที่เลือก</div>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-lg border border-slate-200 p-4">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg bg-emerald-100 flex items-center justify-center">
              <CheckCircle className="w-5 h-5 text-emerald-600" />
            </div>
            <div>
              <div className="text-xs text-slate-600">อัตราครอบคลุมเฉลี่ย</div>
              <div className="text-xl font-bold text-korat-navy-700 tabular-nums">
                {(displayDistricts.reduce((sum, d) => sum + d.coverage_rate, 0) / displayDistricts.length).toFixed(1)}%
              </div>
              <div className="text-xs text-slate-400 mt-1">สัดส่วนประชากรที่มีสิทธิประกัน</div>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-lg border border-slate-200 p-4">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg bg-korat-navy-100 flex items-center justify-center">
              <AlertTriangle className="w-5 h-5 text-korat-navy-700" />
            </div>
            <div>
              <div className="text-xs text-slate-600">โรคเรื้อรังรวม</div>
              <div className="text-xl font-bold text-korat-navy-700 tabular-nums">
                {displayDistricts.reduce((sum, d) => sum + d.ncd_patients, 0).toLocaleString('th-TH')}
              </div>
              <div className="text-xs text-slate-500">ราย</div>
              <div className="text-xs text-slate-400 mt-1">ผู้ป่วย NCD ที่ต้องติดตามต่อเนื่อง</div>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}

/**
 * Health Map with Error Boundary
 * Wraps HealthMapPolygonInner with MapErrorBoundary to handle map errors
 */
export function HealthMapPolygon(props: HealthMapPolygonProps) {
  return (
    <MapErrorBoundary>
      <HealthMapPolygonInner {...props} />
    </MapErrorBoundary>
  )
}
